import type { CreateVariantsPayload, VariantAttribute } from '@/types/product';
import type { CurrencyCode } from '@bbpos/types';
import { rupeeInputToPaise } from '@/utils/price-input';

// One editable row in the variant matrix grid (New Product → Variants step and
// the product page's "Add variants" modal). A variant is a child product
// (parent_product_id set, is_variant true) carrying its own SKU, barcode,
// price, cost and stock; tax / unit / category come from the parent.
//
// `attributes` is a free-form map so a tenant can define whatever dimensions
// their catalogue needs — Gauge / Length for cable, Size / Colour for apparel.
export interface VariantRow {
  id: string;
  attributes: Record<string, string>;
  sku: string;
  barcode: string;
  price: string;
  cost: string;
  stock: string;
  /** Unchecked rows are skipped on save, so a generated grid can be trimmed. */
  enabled: boolean;
}

/** An attribute and the values the user has selected/added for it. */
export interface AttributeDef {
  id: string;
  name: string;
  values: string[];
}

export function makeVariantRow(id: string): VariantRow {
  return { id, attributes: {}, sku: '', barcode: '', price: '', cost: '', stock: '', enabled: true };
}

export function makeAttribute(id: string): AttributeDef {
  return { id, name: '', values: [] };
}

/** Attributes usable for generation: named, with at least one value. */
export function usableAttributes(attrs: AttributeDef[]): AttributeDef[] {
  return attrs.filter((a) => a.name.trim() && a.values.some((v) => v.trim()));
}

/**
 * Cartesian product of every attribute's values — "1.5 sq.mm / Red",
 * "1.5 sq.mm / Blue", "2.5 sq.mm / Red", and so on. Typing each combination by
 * hand is what made variant entry slow; two attributes with 3 and 4 values are
 * 12 rows.
 */
export function generateCombinations(attrs: AttributeDef[]): Array<Record<string, string>> {
  const usable = usableAttributes(attrs);
  if (usable.length === 0) return [];

  return usable.reduce<Array<Record<string, string>>>(
    (acc, attr) => {
      const values = attr.values.map((v) => v.trim()).filter(Boolean);
      return acc.flatMap((combo) =>
        values.map((value) => ({ ...combo, [attr.name.trim()]: value })),
      );
    },
    [{}],
  );
}

/** Stable key for a combination, used to preserve edits across regeneration. */
export function combinationKey(attrs: Record<string, string>): string {
  return Object.entries(attrs)
    .map(([k, v]) => `${k}=${v}`)
    .join('|');
}

/**
 * Case-insensitive, order-independent identity of a combination — how the
 * server decides two combinations are "the same" (`colour=red|size=m`).
 */
export function normalizedCombinationKey(attrs: Record<string, unknown>): string {
  return Object.entries(attrs)
    .map(([k, v]) => `${k.trim().toLowerCase()}=${String(v).trim().toLowerCase()}`)
    .sort()
    .join('|');
}

/**
 * Build rows for every generated combination, carrying over SKU/price/stock
 * already typed against the same combination so regenerating after adding a
 * value doesn't discard the user's work.
 */
export function buildRowsFromCombinations(
  combos: Array<Record<string, string>>,
  existing: VariantRow[],
): VariantRow[] {
  const byKey = new Map(existing.map((r) => [combinationKey(r.attributes), r]));
  return combos.map((attributes, i) => {
    const prior = byKey.get(combinationKey(attributes));
    return prior
      ? { ...prior, attributes }
      : { ...makeVariantRow(`gen-${i}-${combinationKey(attributes)}`), attributes };
  });
}

// Build the variant_attributes object from the filled attribute fields only.
export function buildVariantAttributes(row: VariantRow): Record<string, string> {
  const attrs: Record<string, string> = {};
  for (const [key, value] of Object.entries(row.attributes)) {
    if (key.trim() && value.trim()) attrs[key.trim()] = value.trim();
  }
  return attrs;
}

// Suffix the parent name with the variant's attribute values, e.g.
// "Cotton T-Shirt (L / Red)". Falls back to the bare parent name.
export function variantName(parentName: string, attrs: Record<string, string>): string {
  const values = Object.values(attrs);
  return values.length ? `${parentName} (${values.join(' / ')})` : parentName;
}

// A row counts as "started" once it carries an attribute value or any typed
// field. Blank rows are ignored entirely.
export function isVariantRowStarted(row: VariantRow): boolean {
  return Boolean(
    Object.values(row.attributes).some((v) => v.trim()) ||
      row.sku.trim() ||
      row.barcode.trim() ||
      row.price.trim() ||
      row.cost.trim() ||
      row.stock.trim(),
  );
}

/** Two variants sharing an identical attribute combination cannot both exist. */
export function findDuplicateCombinations(rows: VariantRow[]): string[] {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const row of rows.filter((r) => r.enabled && isVariantRowStarted(r))) {
    const key = combinationKey(buildVariantAttributes(row));
    if (!key) continue;
    if (seen.has(key)) dupes.add(key);
    seen.add(key);
  }
  return Array.from(dupes);
}

// ─── Library helpers ────────────────────────────────────────────────────────

/** Case-insensitive lookup of a library attribute by name. */
export function findLibraryAttribute(
  library: VariantAttribute[],
  name: string,
): VariantAttribute | undefined {
  const needle = name.trim().toLowerCase();
  return needle ? library.find((a) => a.name.toLowerCase() === needle) : undefined;
}

/** Active values of the library attribute with this name, in display order (empty when unknown). */
export function libraryValuesFor(library: VariantAttribute[], name: string): string[] {
  const attr = findLibraryAttribute(library, name);
  if (!attr || !attr.is_active) return [];
  return attr.values.filter((v) => v.is_active).map((v) => v.value);
}

/**
 * Add a value to a list without case-insensitive duplicates. When the value
 * matches one the library already knows, the library's spelling wins so the
 * chip and the saved snapshot agree with the rest of the catalogue.
 */
export function addValueCaseInsensitive(values: string[], value: string, libraryValues: string[] = []): string[] {
  const typed = value.trim();
  if (!typed) return values;
  const canonical = libraryValues.find((v) => v.toLowerCase() === typed.toLowerCase()) ?? typed;
  return values.some((v) => v.toLowerCase() === canonical.toLowerCase()) ? values : [...values, canonical];
}

/**
 * Combinations not already taken by an existing variant (compared the way the
 * server does — case-insensitive, order-independent). `existingKeys` are
 * normalizedCombinationKey()s of the parent's current children.
 */
export function missingCombinations(
  attrs: AttributeDef[],
  existingKeys: string[],
): Array<Record<string, string>> {
  const taken = new Set(existingKeys);
  return generateCombinations(attrs).filter((combo) => !taken.has(normalizedCombinationKey(combo)));
}

/**
 * Client-side mirror of the server's auto-SKU (uppercase, non-alphanumerics →
 * "-", trimmed) so the grid can preview the SKU a blank field will get.
 */
export function previewVariantSku(parentSku: string, attrs: Record<string, string>): string {
  const slug = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return [parentSku, ...Object.values(attrs).map(slug)].filter(Boolean).join('-').slice(0, 100);
}

/**
 * Body for `POST /products/:id/variants`: enabled rows only. Blank SKU /
 * barcode / price / cost / stock are omitted so the server auto-generates the
 * SKU and inherits price, cost and threshold from the parent. Rupee inputs
 * become minor units in `currency` (defaults to INR).
 */
/**
 * Labels of the enabled variant rows with no price typed — a product created
 * "with variants" has no price of its own to fall back on (POS comments item 3).
 */
export function variantsMissingPrice(rows: VariantRow[]): string[] {
  return rows
    .filter((r) => r.enabled && Object.keys(buildVariantAttributes(r)).length > 0 && r.price.trim() === '')
    .map((r) => Object.values(buildVariantAttributes(r)).join(' / '));
}

/**
 * A typed SKU or barcode used by more than one enabled row, as
 * "CS2BAR0001 (S / Red, S / Blue)" — the server refuses the whole matrix for
 * it. Trimmed and exact, like the server; blank fields are skipped (a blank
 * SKU is auto-generated from the combination).
 */
export function findDuplicateIdentifiers(rows: VariantRow[], field: 'sku' | 'barcode'): string[] {
  const byValue = new Map<string, string[]>();
  for (const r of rows) {
    const attrs = buildVariantAttributes(r);
    const value = r[field].trim();
    if (!r.enabled || Object.keys(attrs).length === 0 || !value) continue;
    byValue.set(value, [...(byValue.get(value) ?? []), Object.values(attrs).join(' / ')]);
  }
  return Array.from(byValue)
    .filter(([, labels]) => labels.length > 1)
    .map(([value, labels]) => `${value} (${labels.join(', ')})`);
}

export function buildCreateVariantsPayload(
  rows: VariantRow[],
  currency: CurrencyCode = 'INR',
): CreateVariantsPayload {
  return {
    variants: rows
      .filter((r) => r.enabled && Object.keys(buildVariantAttributes(r)).length > 0)
      .map((r) => {
        const price = rupeeInputToPaise(r.price, currency);
        const cost = rupeeInputToPaise(r.cost, currency);
        return {
          attributes: buildVariantAttributes(r),
          ...(r.sku.trim() ? { sku: r.sku.trim() } : {}),
          ...(r.barcode.trim() ? { barcode: r.barcode.trim() } : {}),
          ...(price !== null ? { price } : {}),
          ...(cost !== null ? { cost } : {}),
          ...(r.stock.trim() ? { stock_quantity: Number(r.stock) } : {}),
        };
      }),
  };
}
